import { useEffect, useState } from "react";
import { FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";

import { fetchOrganizations, fetchSettings, updateOrganization, updateSettings } from "@/lib/api";
import { getErrorMessage } from "@/lib/utils";

function splitLines(value) {
  return value
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
}

function joinLines(items) {
  return Array.isArray(items) ? items.join("\n") : "";
}

function SectionCard({ title, description, children }) {
  return (
    <section className="bg-white border border-slate-200 rounded-md p-6 space-y-4">
      <div className="space-y-1">
        <h2 className="text-sm font-bold uppercase tracking-wider text-slate-400">{title}</h2>
        {description ? <p className="text-sm text-slate-500">{description}</p> : null}
      </div>
      {children}
    </section>
  );
}

function InputField({ label, testId, autoComplete = "off", ...props }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">{label}</label>
      <input
        data-testid={testId}
        autoComplete={autoComplete}
        className="w-full h-12 px-4 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-black/10 bg-white font-medium"
        {...props}
      />
    </div>
  );
}

function TextareaField({ label, testId, rows = 4, hint, ...props }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">{label}</label>
      <textarea
        data-testid={testId}
        rows={rows}
        className="w-full px-4 py-3 text-sm border border-slate-200 rounded-md resize-y focus:outline-none focus:ring-2 focus:ring-black/10 bg-white font-medium"
        {...props}
      />
      {hint ? <p className="text-xs text-slate-500">{hint}</p> : null}
    </div>
  );
}

function ToggleField({ label, description, checked, onChange, testId }) {
  return (
    <label className="flex items-start gap-3 rounded-md border border-slate-200 px-4 py-3">
      <input
        data-testid={testId}
        type="checkbox"
        className="mt-1 rounded"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="space-y-1">
        <span className="block text-sm font-semibold text-slate-800">{label}</span>
        {description ? <span className="block text-xs text-slate-500">{description}</span> : null}
      </span>
    </label>
  );
}

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;

    async function load() {
      setLoading(true);
      setLoadError("");
      try {
        const [settingsData, organizations] = await Promise.all([
          fetchSettings(),
          fetchOrganizations(),
        ]);
        if (!mounted) return;
        const activeOrg = organizations.find((org) => org.is_active) || organizations[0] || null;
        setSettings(settingsData);
        setOrganization(activeOrg);
      } catch (err) {
        if (!mounted) return;
        setLoadError(getErrorMessage(err, "Failed to load settings"));
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, []);

  const handleSave = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const settingsPayload = {
        ...settings,
        branches: settings.branches || [],
        reference_accessories: settings.reference_accessories || [],
        reference_extra_charge_types: settings.reference_extra_charge_types || [],
        reference_vehicle_categories: settings.reference_vehicle_categories || [],
      };

      const requests = [updateSettings(settingsPayload)];
      if (organization?.id) {
        requests.push(
          updateOrganization(organization.id, {
            name: organization.name || "",
            abn: organization.abn || "",
            phone: organization.phone || "",
            address: organization.address || "",
          })
        );
      }

      const [updatedSettings, updatedOrganization] = await Promise.all(requests);
      setSettings(updatedSettings);
      if (updatedOrganization) setOrganization(updatedOrganization);
      toast.success("Business settings saved");
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to save settings"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-sm text-slate-400">Loading settings...</div>
      </div>
    );
  }

  if (loadError || !settings) {
    return (
      <div className="h-full overflow-y-auto" data-testid="admin-settings-page">
        <div className="max-w-3xl mx-auto p-6 lg:p-8">
          <div className="rounded-md border border-red-200 bg-red-50 px-5 py-4 space-y-3">
            <h1 className="text-lg font-bold text-red-900">Could not load settings</h1>
            <p className="text-sm text-red-700">{loadError || "Something went wrong while loading this page."}</p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="inline-flex items-center justify-center h-10 px-4 rounded-md border border-red-200 bg-white text-sm font-bold text-red-700 hover:bg-red-100"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto" data-testid="admin-settings-page">
      <form
        className="max-w-4xl mx-auto p-6 lg:p-8 space-y-8"
        onSubmit={(event) => {
          event.preventDefault();
          handleSave();
        }}
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-black tracking-tight font-heading text-slate-900">Settings</h1>
            <p className="text-sm text-slate-500 mt-1 font-medium">Manage the business details, defaults, and reference data your team uses every day.</p>
          </div>
          <button
            data-testid="settings-save"
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 h-12 px-6 bg-primary text-primary-foreground rounded-md text-sm font-bold hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            <FloppyDisk weight="bold" className="w-5 h-5" /> {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>

        <SectionCard
          title="Business Profile"
          description="Core business details used across the app, PDFs, and customer-facing documents."
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputField
              label="Business Name"
              testId="settings-business-name"
              value={organization?.name || ""}
              onChange={(event) => setOrganization((current) => ({ ...(current || {}), name: event.target.value }))}
            />
            <InputField
              label="ABN"
              testId="settings-business-abn"
              value={organization?.abn || ""}
              onChange={(event) => setOrganization((current) => ({ ...(current || {}), abn: event.target.value }))}
            />
            <InputField
              label="Phone"
              testId="settings-business-phone"
              autoComplete="tel"
              value={organization?.phone || ""}
              onChange={(event) => setOrganization((current) => ({ ...(current || {}), phone: event.target.value }))}
            />
            <InputField
              label="Public Display Name"
              testId="settings-company-name"
              value={settings.company_name || ""}
              onChange={(event) => setSettings((current) => ({ ...current, company_name: event.target.value }))}
            />
          </div>
          <TextareaField
            label="Address"
            testId="settings-business-address"
            rows={3}
            value={organization?.address || ""}
            onChange={(event) => setOrganization((current) => ({ ...(current || {}), address: event.target.value }))}
          />
        </SectionCard>

        <SectionCard
          title="Branches"
          description="Define the branch names your team can reuse in rentals, pickups, returns, and fleet records."
        >
          <TextareaField
            label="Branch List"
            testId="settings-branches"
            rows={6}
            value={joinLines(settings.branches)}
            onChange={(event) => setSettings((current) => ({ ...current, branches: splitLines(event.target.value) }))}
            hint="One branch per line, for example: Sydney, Parramatta, Newcastle."
          />
        </SectionCard>

        <SectionCard
          title="Customer Requirements"
          description="Choose what customers must provide during agreement completion and signing."
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <ToggleField
              label="Require mobile number"
              description="Ask every customer for a contact mobile before signing."
              checked={Boolean(settings.require_customer_mobile)}
              onChange={(value) => setSettings((current) => ({ ...current, require_customer_mobile: value }))}
              testId="settings-require-customer-mobile"
            />
            <ToggleField
              label="Require address"
              description="Require a street address before the agreement can be completed."
              checked={Boolean(settings.require_customer_address)}
              onChange={(value) => setSettings((current) => ({ ...current, require_customer_address: value }))}
              testId="settings-require-customer-address"
            />
            <ToggleField
              label="Require postcode"
              description="Keep postcode mandatory as part of the customer address."
              checked={Boolean(settings.require_customer_postcode)}
              onChange={(value) => setSettings((current) => ({ ...current, require_customer_postcode: value }))}
              testId="settings-require-customer-postcode"
            />
            <ToggleField
              label="Require licence number"
              description="Collect the customer's driver licence number before signing."
              checked={Boolean(settings.require_customer_licence_number)}
              onChange={(value) => setSettings((current) => ({ ...current, require_customer_licence_number: value }))}
              testId="settings-require-customer-licence-number"
            />
            <ToggleField
              label="Require licence photo"
              description="Make the licence photo upload mandatory before signature submission."
              checked={Boolean(settings.require_customer_licence_photo)}
              onChange={(value) => setSettings((current) => ({ ...current, require_customer_licence_photo: value }))}
              testId="settings-require-customer-licence-photo"
            />
          </div>
        </SectionCard>

        <SectionCard
          title="Reference Data"
          description="Maintain the reusable lists your team selects from during daily operations."
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <TextareaField
              label="Vehicle Categories"
              testId="settings-reference-vehicle-categories"
              rows={7}
              value={joinLines(settings.reference_vehicle_categories)}
              onChange={(event) => setSettings((current) => ({ ...current, reference_vehicle_categories: splitLines(event.target.value) }))}
              hint="One value per line."
            />
            <TextareaField
              label="Accessories"
              testId="settings-reference-accessories"
              rows={7}
              value={joinLines(settings.reference_accessories)}
              onChange={(event) => setSettings((current) => ({ ...current, reference_accessories: splitLines(event.target.value) }))}
              hint="These can become your standard inspection checklist items."
            />
            <TextareaField
              label="Extra Charge Types"
              testId="settings-reference-extra-charge-types"
              rows={7}
              value={joinLines(settings.reference_extra_charge_types)}
              onChange={(event) => setSettings((current) => ({ ...current, reference_extra_charge_types: splitLines(event.target.value) }))}
              hint="Examples: fuel charge, late fee, cleaning fee."
            />
          </div>
        </SectionCard>
      </form>
    </div>
  );
}
